import {useQuery, type UseQueryOptions} from '@tanstack/react-query'
import {type AxiosError} from 'axios'

import {riskSurveyQueries} from '../../api'
import type {ReportSummaryDto} from '../../types'

import {riskSurveyQueryKeys} from './riskSurveyQueryKeys'
import type {ReportListParams} from './useReports'

const ALL_REPORTS_PAGE_SIZE = 100

type AllReportParams = Omit<ReportListParams, 'page' | 'pageSize'>

export const useAllReports = (
  params: AllReportParams,
  options?: Omit<UseQueryOptions<ReportSummaryDto[], AxiosError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<ReportSummaryDto[], AxiosError>({
    queryKey: riskSurveyQueryKeys.reports.allList(params),
    queryFn: async ({signal}) => {
      const firstPage = await riskSurveyQueries.getReports(
        {
          ...params,
          page: 1,
          pageSize: ALL_REPORTS_PAGE_SIZE
        },
        signal
      )

      if (firstPage.totalPages <= 1) {
        return firstPage.items
      }

      const remainingPages = Array.from({length: firstPage.totalPages - 1}, (_, index) => {
        return riskSurveyQueries.getReports(
          {
            ...params,
            page: index + 2,
            pageSize: ALL_REPORTS_PAGE_SIZE
          },
          signal
        )
      })

      const remainingResults = await Promise.all(remainingPages)

      return [firstPage, ...remainingResults].flatMap((page) => page.items)
    },
    staleTime: 30_000,
    gcTime: 300_000,
    ...options
  })
}
